import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Search, ArrowRight, TrendingUp, Layers, Cpu, Building2, MapPin, RotateCcw, FileText, ShieldCheck } from "lucide-react";
import { api, API } from "../lib/api";
import { Link } from "react-router-dom";

const NAVY = "#0F2A43";
const aed = (n) => "AED " + Number(n).toLocaleString();

const Reveal = ({ children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}>
    {children}
  </motion.div>
);

const Act = React.forwardRef(({ children, id, className = "" }, ref) => (
  <section ref={ref} id={id} data-testid={`vision-${id}`}
    className={`min-h-screen flex flex-col justify-center px-6 lg:px-20 py-24 snap-start ${className}`}>
    {children}
  </section>
));

const Label = ({ children }) => (
  <span className="text-[11px] uppercase tracking-[0.25em] text-gold/70">{children}</span>
);

const ProjectionTag = () => (
  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-amber-400/10 text-amber-300 border border-amber-400/25">
    Roadmap — projections, not commitments
  </span>
);

// ---- Act 2: live CMA ----
const LiveCMA = () => {
  const options = [
    { area: "Dubai Marina", building: "Princess Tower", size: 1180, asking: 2600000 },
    { area: "Dubai Marina", building: "Marina Gate", size: 1180, asking: 1850000 },
    { area: "Downtown Dubai", building: "Burj Vista", size: 1300, asking: 3100000 },
  ];
  const [sel, setSel] = useState(0);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async (i) => {
    const o = options[i];
    setLoading(true); setRes(null);
    try {
      const { data } = await api.get("/broker/cma/preview", {
        params: { area: o.area, building: o.building, bedrooms: 2, size_sqft: o.size, asking_price: o.asking, property_type: "Apartment" },
      });
      setRes(data);
    } catch { setRes({ error: true }); } finally { setLoading(false); }
  }, []);
  useEffect(() => { run(0); }, [run]);

  return (
    <div className="rounded-2xl border border-white/10 bg-surface/70 overflow-hidden">
      <div className="flex flex-wrap gap-2 p-4 border-b border-white/10">
        {options.map((o, i) => (
          <button key={o.building} onClick={() => { setSel(i); run(i); }} data-testid="vision-cma-option"
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${sel === i ? "border-gold text-gold bg-gold/10" : "border-white/10 text-white/60 hover:border-gold/40"}`}>
            {o.building}
          </button>
        ))}
      </div>
      <div className="p-5 relative">
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.06] select-none">
          <span className="font-display text-6xl rotate-[-18deg] text-gold">DEMO DATA</span>
        </div>
        {loading && <div className="text-white/50 text-sm">Computing comparable market analysis…</div>}
        {res && !res.error && !res.insufficient && (
          <div className="relative">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[["Median AED/sqft", Number(res.median_psf).toLocaleString(), true],
                ["Deviation vs asking", `${res.deviation_pct > 0 ? "+" : ""}${res.deviation_pct}%`, Math.abs(res.deviation_pct) > 8],
                ["Confidence", String(res.confidence).replace(/^./, (c) => c.toUpperCase()), false],
                ["Comparables", res.comp_count, false]].map(([l, v, a]) => (
                <div key={l} className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2">
                  <div className="text-[9px] uppercase tracking-wider text-white/40">{l}</div>
                  <div className={`text-sm font-semibold mt-0.5 ${a ? "text-gold" : "text-white"}`}>{v}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 rounded-lg bg-gold/5 border border-gold/15 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-white/40">Suggested range</div>
              <div className="font-display text-gold">{aed(res.suggested_range_aed[0])} — {aed(res.suggested_range_aed[1])}</div>
            </div>
            {/* Traceability panel */}
            <details className="mt-3 group" open>
              <summary className="cursor-pointer text-xs text-gold/90 flex items-center gap-1.5"><ShieldCheck size={13} /> Traceability — every figure ties to a source transaction</summary>
              <div className="mt-2 text-[11px] text-white/55 leading-relaxed">
                Level {res.level_used} · {res.comp_count} qualifying comps · {res.excluded_count} evaluated & excluded with reasons on file.
                <div className="mt-1 font-mono text-white/40 break-all">Source txns: {(res.source_txn_ids || []).join(", ")}</div>
              </div>
            </details>
            <p className="mt-3 text-[11px] text-white/40">Deterministic engine. The AI never invents a number. Based on DEMO sample data — Live DLD-data mode ships on completion of data licensing.</p>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-3 p-4 border-t border-white/10">
        <Link to="/ask" state={{ prompt: `Is ${aed(options[sel].asking)} fair for a 2BR in ${options[sel].building}, ${options[sel].area}, ${options[sel].size} sqft?` }}
          data-testid="vision-to-ask" className="inline-flex items-center gap-1.5 text-sm text-gold border border-gold/30 rounded-full px-4 py-2 hover:bg-gold/10 transition-colors">
          See the AI cite these comps <ArrowRight size={14} />
        </Link>
        <a href={`${API}/broker/cma/sample`} target="_blank" rel="noreferrer" data-testid="vision-pdf"
          className="inline-flex items-center gap-1.5 text-sm text-white/70 border border-white/10 rounded-full px-4 py-2 hover:border-gold/40 hover:text-gold transition-colors">
          <FileText size={14} /> The exported PDF
        </a>
      </div>
    </div>
  );
};

// ---- Act 3: traction ----
const Traction = () => {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/vision/metrics").then((r) => setM(r.data)).catch(() => {}); }, []);
  if (!m) return <div className="text-white/40 text-sm">Loading live metrics…</div>;
  const cards = [
    ["CMAs run", m.metrics.cmas_run], ["Projects created", m.metrics.projects_created],
    ["Pilot brokers onboarded", m.metrics.pilot_brokers_onboarded],
    ["Broker applications pending", m.metrics.pilot_applications_pending],
    ["Waitlist signups", m.metrics.waitlist_total],
  ];
  return (
    <div data-testid="vision-traction-panel">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map(([l, v]) => (
          <div key={l} className="rounded-2xl bg-surface/70 border border-white/10 p-5">
            <div className="font-display text-4xl text-gold">{Number(v).toLocaleString()}</div>
            <div className="text-xs text-white/55 mt-1">{l}</div>
          </div>
        ))}
      </div>
      {m.waitlist_by_vertical?.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2">
          {m.waitlist_by_vertical.map((w) => (
            <span key={w.capability} className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-white/60">
              {w.name} <span className="text-gold">{w.count}</span>
            </span>
          ))}
        </div>
      )}
      <p className="text-xs text-white/40 mt-4">Live figures from the production database as of {new Date(m.as_of).toLocaleString()} · build {m.build}. Early and honest — no invented users, GMV or growth curves.</p>
    </div>
  );
};

// ---- Act 4: architecture ----
const layers = [
  ["User", "One person, one conversation — no category picker.", MapPin],
  ["BuyDubai.ai", "The single conversational surface for the city.", Search],
  ["Buy-Tech.ai Orchestrator", "Routes intent to the right specialist, silently.", Cpu],
  ["Specialist Agents", "Property (live), Services, Business, Relocation… each an expert.", Layers],
  ["Shared Tool Layer", "Commerce · City Intelligence Graph · Official Services.", Building2],
  ["Unified response", "One answer, fully cited — facts only from tools.", ArrowRight],
];

export default function Vision() {
  const refs = useRef([]);
  const cmaRef = useRef(null);

  const scrollTo = useCallback((i) => {
    const el = refs.current[i];
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const cur = refs.current.findIndex((el) => el && el.getBoundingClientRect().top >= -window.innerHeight / 2 && el.getBoundingClientRect().top < window.innerHeight / 2);
      const idx = cur === -1 ? 0 : cur;
      if (e.key === "ArrowDown" || e.key === "PageDown") { e.preventDefault(); scrollTo(Math.min(idx + 1, 5)); }
      if (e.key === "ArrowUp" || e.key === "PageUp") { e.preventDefault(); scrollTo(Math.max(idx - 1, 0)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [scrollTo]);

  const reset = () => { scrollTo(0); };
  const setRef = (i) => (el) => { refs.current[i] = el; };

  return (
    <div className="snap-y snap-mandatory" style={{ background: NAVY }} data-testid="vision-page">
      <button onClick={reset} data-testid="vision-reset"
        className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full bg-black/40 backdrop-blur border border-white/15 text-white/70 hover:text-gold hover:border-gold/40 transition-colors">
        <RotateCcw size={13} /> Reset demo
      </button>

      {/* Act 1 — problem */}
      <Act ref={setRef(0)} id="act1">
        <Reveal><Label>Act I · The problem</Label></Reveal>
        <Reveal delay={0.05}>
          <h1 className="font-display text-4xl sm:text-6xl text-white mt-4 max-w-4xl leading-tight">
            Buying anything important in Dubai means 12 websites, 40 filters, and endless WhatsApp threads.
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 max-w-xl">
            <div className="flex items-center gap-3 rounded-2xl border border-gold/30 bg-black/20 px-5 py-4">
              <Search size={20} className="text-gold" />
              <span className="font-display text-2xl text-white">Ask Dubai. Get it done.</span>
            </div>
            <p className="text-white/50 mt-3 text-sm">One input replaces the mess. That input is BuyDubai.ai.</p>
          </div>
        </Reveal>
      </Act>

      {/* Act 2 — the engine (live) */}
      <Act ref={setRef(1)} id="act2">
        <Reveal><Label>Act II · The engine — live &amp; real</Label></Reveal>
        <Reveal delay={0.05}><h2 className="font-display text-3xl sm:text-5xl text-white mt-4 max-w-3xl">A deterministic pricing engine, running right now.</h2></Reveal>
        <Reveal delay={0.1}><p className="text-white/55 mt-3 max-w-2xl">Pick a building. Watch the median, range and comparables compute — with the traceability panel open. Then see the AI cite the exact same figures, and the seller-ready PDF.</p></Reveal>
        <Reveal delay={0.15}><div ref={cmaRef} className="mt-8 max-w-2xl"><LiveCMA /></div></Reveal>
      </Act>

      {/* Act 3 — traction */}
      <Act ref={setRef(2)} id="act3">
        <Reveal><Label>Act III · Real traction</Label></Reveal>
        <Reveal delay={0.05}><h2 className="font-display text-3xl sm:text-5xl text-white mt-4">Small, real, and growing — from the live database.</h2></Reveal>
        <Reveal delay={0.1}><div className="mt-8 max-w-4xl"><Traction /></div></Reveal>
      </Act>

      {/* Act 4 — architecture */}
      <Act ref={setRef(3)} id="act4">
        <Reveal><Label>Act IV · The architecture</Label></Reveal>
        <Reveal delay={0.05}><h2 className="font-display text-3xl sm:text-5xl text-white mt-4">One platform. Every city is configuration.</h2></Reveal>
        <div className="mt-10 grid gap-3 max-w-3xl">
          {layers.map(([t, d, Icon], i) => (
            <Reveal key={t} delay={i * 0.06}>
              <div className="group flex items-center gap-4 rounded-xl border border-white/10 bg-surface/60 px-5 py-4 hover:border-gold/40 transition-colors">
                <Icon size={18} className="text-gold shrink-0" />
                <div className="font-medium text-white w-56 shrink-0">{t}</div>
                <div className="text-sm text-white/50 opacity-0 group-hover:opacity-100 transition-opacity">{d}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </Act>

      {/* Act 5 — the arc */}
      <Act ref={setRef(4)} id="act5">
        <Reveal><Label>Act V · The arc</Label></Reveal>
        <Reveal delay={0.05}><div className="mt-3"><ProjectionTag /></div></Reveal>
        <Reveal delay={0.1}><h2 className="font-display text-3xl sm:text-5xl text-white mt-4 max-w-3xl">Dubai today. The region next. Gateway cities after.</h2></Reveal>
        <div className="mt-10 grid md:grid-cols-3 gap-4 max-w-4xl">
          {[["City #1 — live", ["Dubai"], "gold"],
            ["GCC — next", ["Abu Dhabi", "Riyadh", "Doha", "Kuwait City"], "white"],
            ["Gateway cities — later", ["Singapore", "London", "Miami"], "muted"]].map(([h, cities, tone], i) => (
            <Reveal key={h} delay={i * 0.08}>
              <div className="rounded-2xl border border-white/10 bg-surface/60 p-5">
                <div className={`text-sm font-semibold ${tone === "gold" ? "text-gold" : tone === "white" ? "text-white" : "text-white/50"}`}>{h}</div>
                <div className="mt-3 space-y-2">
                  {cities.map((c) => (
                    <div key={c} className="flex items-center gap-2 text-sm text-white/70">
                      <span className={`w-2 h-2 rounded-full ${tone === "gold" ? "bg-gold animate-pulse" : tone === "white" ? "bg-white/60" : "bg-white/25"}`} /> {c}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.3}><p className="text-white/45 text-sm mt-6 max-w-2xl">Every city is configuration, not a rebuild. Buy-Tech.ai is the platform; BuyDubai.ai is city #1.</p></Reveal>
      </Act>

      {/* Act 6 — the ask */}
      <Act ref={setRef(5)} id="act6">
        <Reveal><Label>Act VI · The ask</Label></Reveal>
        <Reveal delay={0.05}><h2 className="font-display text-3xl sm:text-5xl text-white mt-4">Let's build the AI front door to Dubai.</h2></Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8 rounded-2xl border border-gold/25 bg-surface/60 p-7 max-w-xl">
            <p className="text-white/70">For the deck, the data room, or a live walkthrough of the engine:</p>
            <a href="mailto:invest@buydubai.ai" data-testid="vision-contact"
              className="mt-4 inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-gold text-ink font-semibold hover:brightness-110 transition-all">
              invest@buydubai.ai <ArrowRight size={15} />
            </a>
            <p className="text-white/40 text-xs mt-4">Powered by Buy-Tech.ai. Every fact on this page is real; every projection is labeled.</p>
          </div>
        </Reveal>
      </Act>
    </div>
  );
}
